
import { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import debounce from "lodash.debounce";
import "leaflet/dist/leaflet.css";

/* ------------------------------------------------------------------ */
/* 1. Icone personalizzate                                             */
/* ------------------------------------------------------------------ */

import icon1 from "../assets/icon/pin/PinFill/pinFillArancione.png";
import icon2 from "../assets/icon/pin/PinFill/pinFillBlu.png";
import icon3 from "../assets/icon/pin/PinFill/pinFillGiallo.png";
import icon4 from "../assets/icon/pin/PinFill/pinFillRosa.png";
import icon5 from "../assets/icon/pin/PinFill/pinFillTurchese.png";
import icon6 from "../assets/icon/pin/PinFill/pinFillVerde.png";
import icon7 from "../assets/icon/pin/PinFill/pinFillViola.png";
import icon8 from "../assets/icon/pin/PinFill/PinFillRosso.png"
import ACTIVITY_OPTIONS from "../components/utils/ActivityOptions";

const activityIconMapping = {
  potatura: icon1,
  abbattimento: icon2,
  reti_recinzione: icon3,
  sfalcio: icon4,
  reti_paramassi: icon5,
  diserbo: icon6,
  censimento_arboreo: icon7,
  pulizia_strada:  icon8
};

/* ------------------------------------------------------------------ */
/* 2. Fix icone default Leaflet                                        */
/* ------------------------------------------------------------------ */

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

/* ------------------------------------------------------------------ */
/* 3. Helpers                                                          */
/* ------------------------------------------------------------------ */

const getActivityIcon = (activity) => {
  let iconUrl;
  for (const name of activity.activity_names) {
    const key = name.toLowerCase().replace(/\s+/g, "_");
    if (activityIconMapping[key]) {
      iconUrl = activityIconMapping[key];
      break;
    }
  }
  if (!iconUrl) iconUrl = require("leaflet/dist/images/marker-icon.png");

  return L.icon({
    iconUrl,
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
    iconSize: [25, 30],
    iconAnchor: [12, 25],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });
};

const formatActivityNames = (names) =>
  names.map((name) => {
    const opt = ACTIVITY_OPTIONS.find(
      (o) => o.value === name.toLowerCase()
    );
    return opt ? opt.label : name;
  });

/* ------------------------------------------------------------------ */
/* 4. Componenti interni                                               */
/* ------------------------------------------------------------------ */

function LocationMarker({ setPosition }) {
  useMapEvents({
    click(e) {
      setPosition?.(e.latlng);
    },
  });
  return null;
}

function MapFlyTo({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) setTimeout(() => map.flyTo(position, 16), 100);
  }, [position, map]);
  return null;
}

/* ------------------------------------------------------------------ */
/* 5. Componente principale                                            */
/* ------------------------------------------------------------------ */

export default function LeafletMap({
  position,
  setPosition,
  onLocationInfo,
  activities,
}) {
  const [address, setAddress] = useState(null);

  /* cache risultati reverse-geocoding */
  const cacheRef = useRef(new Map());

  /* funzione debounced (≤1 req / 1,2 s) */
  const debouncedReverse = useRef(
    debounce(async (lat, lng) => {
      const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;

      /* 1) cache hit */
      if (cacheRef.current.has(key)) {
        const data = cacheRef.current.get(key);
        setAddress(data);
        onLocationInfo?.(data);
        return;
      }

      /* 2) chiamata al server */
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&email=dev@example.com`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        cacheRef.current.set(key, data);
        setAddress(data);
        onLocationInfo?.(data);
      } catch (err) {
        console.error("Errore reverse-geocoding:", err);
      }
    }, 1200)
  ).current;

  useEffect(() => {
    if (position) debouncedReverse(position.lat, position.lng);
  }, [position, debouncedReverse]);

  useEffect(() => () => debouncedReverse.cancel(), [debouncedReverse]);

  const defaultPosition = [41.9028, 12.4964]; // Roma

  /* ---------------------------------------------------------------- */
  /* 6. Render                                                         */
  /* ---------------------------------------------------------------- */

  return (
    <div className="w-full h-screen">
      <MapContainer
        center={
          position ? [position.lat, position.lng] : defaultPosition
        }
        zoom={position ? 16 : 13}
        scrollWheelZoom
        className="h-full w-full z-0"
      >
        <TileLayer
          url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
          attribution="&copy; Google Maps"
        />

        {setPosition && <LocationMarker setPosition={setPosition} />}

        {position && (
          <>
            <MapFlyTo position={position} />
            <Marker position={position}>
              <Popup>
                {address ? (
                  <>
                    <strong>Indirizzo selezionato:</strong>
                    <br />
                    {address.display_name}
                  </>
                ) : (
                  "Hai selezionato questo punto"
                )}
              </Popup>
            </Marker>
          </>
        )}

        {activities?.map((a) => {
          const lat = +a.latitude;
          const lng = +a.longitude;
          if (Number.isNaN(lat) || Number.isNaN(lng)) {
            console.warn(
              `Coordinata non valida (id ${a.id}):`,
              a.latitude,
              a.longitude
            );
            return null;
          }

          const names = formatActivityNames(a.activity_names);

          return (
            <Marker
              key={a.id}
              position={[lat, lng]}
              icon={getActivityIcon(a)}
              eventHandlers={{
                click: (e) => e.originalEvent.stopPropagation(),
              }}
            >
              <Popup>
                <strong>{names.join(", ")}</strong>
                <br />
                {new Date(a.start_date).toLocaleDateString("it-IT")} –{" "}
                {new Date(a.end_date).toLocaleDateString("it-IT")}
                <br />
                {a.location_info}
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}
